import { mergeTranslations } from "ra-core";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import norwegianMessages from "ra-language-norwegian";
import { raSupabaseEnglishMessages } from "ra-supabase-language-english";
import { englishCrmMessages } from "./englishCrmMessages";
import { englishExtraMessages } from "./englishExtraMessages";
import { englishUnbilledReviewMessages } from "./unbilledReviewMessages";
import { englishMacroPlanMessages } from "./macroPlanMessages";
import {
  englishOversiktFokusMessages,
  norwegianOversiktFokusMessages,
} from "./oversiktFokusMessages";
import { norwegianCrmMessages } from "./norwegianCrmMessages";
import { norwegianExtraMessages } from "./norwegianExtraMessages";
import {
  englishPipelineMessages,
  norwegianPipelineMessages,
} from "./pipelineMessages";
import {
  englishPipelineLostMessages,
  norwegianPipelineLostMessages,
} from "./pipelineLostMessages";
import {
  englishPipelineReopenMessages,
  norwegianPipelineReopenMessages,
} from "./pipelineReopenMessages";
import {
  englishPipelineWonMessages,
  norwegianPipelineWonMessages,
} from "./pipelineWonMessages";
import {
  englishPipelineHandoverMessages,
  norwegianPipelineHandoverMessages,
} from "./pipelineHandoverMessages";
import {
  englishPipelineTableMessages,
  norwegianPipelineTableMessages,
} from "./pipelineTableMessages";
import {
  englishPipelineTodayMessages,
  norwegianPipelineTodayMessages,
} from "./pipelineTodayMessages";
import {
  englishPipelineLeadMessages,
  norwegianPipelineLeadMessages,
} from "./pipelineLeadMessages";
import {
  englishPipelineViewMessages,
  norwegianPipelineViewMessages,
} from "./pipelineViewMessages";
import {
  englishPipelineIntakeMessages,
  norwegianPipelineIntakeMessages,
} from "./pipelineIntakeMessages";
import {
  englishPipelineReportMessages,
  norwegianPipelineReportMessages,
} from "./pipelineReportMessages";
import {
  englishPipelineMobileMessages,
  norwegianPipelineMobileMessages,
} from "./pipelineMobileMessages";
import {
  englishPipelineRestMessages,
  norwegianPipelineRestMessages,
} from "./pipelineRestMessages";

const raSupabaseEnglishMessagesOverride = {
  "ra-supabase": {
    auth: {
      password_reset: "Check your emails for a Reset Password message.",
    },
  },
};

const englishBase = mergeTranslations(
  englishMessages,
  raSupabaseEnglishMessages,
  raSupabaseEnglishMessagesOverride,
  englishCrmMessages,
  englishPipelineMessages,
  englishPipelineViewMessages,
  englishPipelineTodayMessages,
  englishPipelineLostMessages,
  englishPipelineReopenMessages,
  englishPipelineWonMessages,
  englishPipelineHandoverMessages,
  englishPipelineTableMessages,
  englishPipelineIntakeMessages,
  englishPipelineLeadMessages,
  englishPipelineReportMessages,
  englishPipelineMobileMessages,
  englishPipelineRestMessages,
  englishOversiktFokusMessages,
);

export const englishCatalog = mergeTranslations(
  englishExtraMessages,
  englishUnbilledReviewMessages,
  englishMacroPlanMessages,
  englishBase,
);

// Norsk: engelsk katalog som fallback-base, så react-admins norske base-pakke
// (ra-language-norwegian) for rammeverk-strenger, så Mimins egne norske app-
// strenger øverst. Manglende norske nøkler faller tilbake til engelsk.
// englishExtraMessages er ikke med: de nøklene har norsk fallback i koden,
// og den skal vinne på norsk.
const norwegianCatalog = mergeTranslations(
  englishBase,
  norwegianMessages,
  norwegianExtraMessages,
  norwegianCrmMessages,
  norwegianPipelineMessages,
  norwegianPipelineViewMessages,
  norwegianPipelineTodayMessages,
  norwegianPipelineLostMessages,
  norwegianPipelineReopenMessages,
  norwegianPipelineWonMessages,
  norwegianPipelineHandoverMessages,
  norwegianPipelineTableMessages,
  norwegianPipelineIntakeMessages,
  norwegianPipelineLeadMessages,
  norwegianPipelineReportMessages,
  norwegianPipelineMobileMessages,
  norwegianPipelineRestMessages,
  norwegianOversiktFokusMessages,
);

// Mimin er norsk-først: nb er standard uansett nettleserspråk. Engelsk er
// valgbart, men brukeren velger det selv via språkvelgeren på profilsiden. Vi
// auto-bytter IKKE til nettleserens språk (ellers får norske brukere med engelsk
// nettleser et engelsk grensesnitt).
export const getInitialLocale = (): "nb" | "en" => "nb";

export const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "en") {
      return englishCatalog;
    }
    return norwegianCatalog;
  },
  getInitialLocale(),
  [
    { locale: "nb", name: "Norsk" },
    { locale: "en", name: "English" },
  ],
  { allowMissing: true },
);

// Testene er skrevet mot de norske fallback-tekstene i koden, så de bruker
// den engelske katalogen uten englishExtraMessages. Dekningen av den testes i
// i18nCoverage.test.ts.
export const testI18nProvider = polyglotI18nProvider(
  () => englishBase,
  "en",
  [{ locale: "en", name: "English" }],
  { allowMissing: true },
);
